import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, FilePlus2, PanelLeft } from "lucide-react";
import { useMobile } from "../../hooks/useMobile";
import {
  createText,
  reorderTexts,
  subscribeWorkspace,
  toSortedArray,
  updateText,
} from "../../services/workspaceService";
import { useEditLock } from "../../context/EditLockContext";
import { getRememberedWritingText, rememberWritingText } from "../../utils/writingSession";
import { navigateToText } from "../../utils/textRoutes";

const EMPTY_EDITOR_HTML = "<p><br></p>";

function editorSeedTitle(value) {
  return value || " ";
}

function editorSeedContent(value) {
  return value || EMPTY_EDITOR_HTML;
}

function normalizeEditorContent(value) {
  const html = String(value || "");
  const meaningfulMedia = /<(img|aside|audio|video|iframe)\b/i.test(html);
  const plain = html
    .replace(/<br\s*\/?>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]*>/g, "")
    .trim();
  return !meaningfulMedia && !plain ? "" : html;
}

function findText(workspace, textId) {
  if (!textId) return null;
  for (const desk of workspace?.desks || []) {
    for (const drawer of toSortedArray(desk.drawers)) {
      for (const notebook of toSortedArray(drawer.notebooks)) {
        const texts = toSortedArray(notebook.texts);
        const text = texts.find((item) => item.id === textId);
        if (!text) continue;
        return {
          ...text,
          deskId: desk.id,
          deskName: desk.name,
          deskSlug: desk.slug,
          drawerId: drawer.id,
          drawerName: drawer.name,
          drawerSlug: drawer.slug,
          notebookId: notebook.id,
          notebookName: notebook.name,
          notebookSlug: notebook.slug,
          notebookColor: notebook.color,
          _notebookTexts: texts,
        };
      }
    }
  }
  return null;
}

function manuscriptOrder(texts) {
  const list = [...(texts || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const byParent = new Map();
  for (const text of list) {
    const key = text.parentId || "__root__";
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(text);
  }

  const ordered = [];
  const seen = new Set();
  const visit = (parentId = null) => {
    const kids = byParent.get(parentId || "__root__") || [];
    for (const text of kids) {
      if (seen.has(text.id)) continue;
      seen.add(text.id);
      if (text.kind !== "folder") ordered.push(text);
      visit(text.id);
    }
  };
  visit(null);

  for (const text of list) {
    if (text.kind !== "folder" && !seen.has(text.id)) ordered.push(text);
  }
  return ordered;
}

function readLiveEditor(current) {
  if (!current) return null;
  const titleEl = document.querySelector('input[placeholder="Sin título..."]');
  const canvas = document.querySelector("[data-article-canvas]");
  if (!titleEl && !canvas) return null;

  let content = current.content || "";
  if (canvas) {
    const clone = canvas.cloneNode(true);
    clone.querySelector(".dictation-tentative")?.remove();
    content = normalizeEditorContent(clone.innerHTML);
  }

  const liveTitle = titleEl?.value ?? current.title ?? "";
  return {
    title: liveTitle === " " ? "" : liveTitle,
    content,
  };
}

export default function ManuscriptNavigator() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMobile();
  const { canEdit } = useEditLock();
  const [workspace, setWorkspace] = useState({ desks: [] });
  const [currentId, setCurrentId] = useState(() => getRememberedWritingText()?.id || null);
  const [busy, setBusy] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState("");
  const feedbackTimerRef = useRef(null);

  const isEditorRoute = location.pathname === "/editor" || location.pathname.startsWith("/editor/");
  const activeRoute =
    isEditorRoute ||
    location.pathname.startsWith("/a/") ||
    location.pathname.startsWith("/notas/");

  useEffect(() => subscribeWorkspace(setWorkspace), []);

  useEffect(() => {
    const sync = () => setCurrentId(getRememberedWritingText()?.id || null);
    sync();
    window.addEventListener("trufapp:writing-text", sync);
    return () => window.removeEventListener("trufapp:writing-text", sync);
  }, [location.pathname]);

  useEffect(() => () => {
    if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
  }, []);

  const current = useMemo(() => findText(workspace, currentId), [workspace, currentId]);
  const sequence = useMemo(
    () => (current ? manuscriptOrder(current._notebookTexts) : []),
    [current],
  );
  const sequenceIndex = current ? sequence.findIndex((item) => item.id === current.id) : -1;
  const previous = sequenceIndex > 0 ? sequence[sequenceIndex - 1] : null;
  const next = sequenceIndex >= 0 && sequenceIndex < sequence.length - 1 ? sequence[sequenceIndex + 1] : null;

  const flashSave = useCallback((message) => {
    setSaveFeedback(message);
    if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = window.setTimeout(() => setSaveFeedback(""), 1600);
  }, []);

  const persistLiveEditor = useCallback(async () => {
    if (!current || !isEditorRoute) return false;
    const live = readLiveEditor(current);
    if (!live) return false;
    if (live.title === (current.title || "") && live.content === (current.content || "")) return true;
    await updateText(current.deskId, current.drawerId, current.notebookId, current.id, live);
    return true;
  }, [current, isEditorRoute]);

  const saveInPlace = useCallback(async () => {
    if (!current || busy) return;
    setBusy(true);
    try {
      await persistLiveEditor();
      flashSave("Guardado ✓");
    } catch (error) {
      console.error("[ManuscriptNavigator] save failed:", error);
      flashSave("Error al guardar");
    } finally {
      setBusy(false);
    }
  }, [busy, current, flashSave, persistLiveEditor]);

  const openInContext = useCallback(
    async (target) => {
      if (!target || busy) return;
      setBusy(true);
      try {
        await persistLiveEditor();
        const enriched = findText(workspace, target.id);
        if (!enriched) return;
        rememberWritingText(enriched);
        if (isEditorRoute) {
          navigate("/editor", {
            state: {
              autoStart: false,
              deskId: enriched.deskId,
              drawerId: enriched.drawerId,
              notebookId: enriched.notebookId,
              textId: enriched.id,
              initialTitle: editorSeedTitle(enriched.title),
              initialContent: editorSeedContent(enriched.content),
              initialImg: enriched.img,
              mode: "edit",
            },
          });
        } else {
          navigateToText(navigate, enriched);
        }
      } catch (error) {
        console.error("[ManuscriptNavigator] navigation failed:", error);
      } finally {
        setBusy(false);
      }
    },
    [busy, isEditorRoute, navigate, persistLiveEditor, workspace],
  );

  const createNext = useCallback(async () => {
    if (!current || !canEdit || busy) return;
    setBusy(true);
    try {
      await persistLiveEditor();
      const parentId = current.kind === "folder" ? current.id : current.parentId || null;
      const newId = await createText(
        current.deskId,
        current.drawerId,
        current.notebookId,
        "",
        "",
        null,
        { kind: "note", ...(parentId ? { parentId } : {}) },
      );

      const ids = current._notebookTexts.map((item) => item.id).filter((id) => id !== newId);
      const currentIndex = ids.indexOf(current.id);
      ids.splice(currentIndex >= 0 ? currentIndex + 1 : ids.length, 0, newId);
      await reorderTexts(current.deskId, current.drawerId, current.notebookId, ids);

      const created = {
        id: newId,
        title: "",
        content: "",
        kind: "note",
        parentId: parentId || undefined,
        deskId: current.deskId,
        deskName: current.deskName,
        deskSlug: current.deskSlug,
        drawerId: current.drawerId,
        drawerName: current.drawerName,
        drawerSlug: current.drawerSlug,
        notebookId: current.notebookId,
        notebookName: current.notebookName,
        notebookSlug: current.notebookSlug,
      };
      rememberWritingText(created);
      navigate("/editor", {
        state: {
          autoStart: false,
          deskId: created.deskId,
          drawerId: created.drawerId,
          notebookId: created.notebookId,
          textId: created.id,
          initialTitle: editorSeedTitle(created.title),
          initialContent: editorSeedContent(created.content),
          mode: "edit",
        },
      });
    } catch (error) {
      console.error("[ManuscriptNavigator] create next failed:", error);
    } finally {
      setBusy(false);
    }
  }, [busy, canEdit, current, navigate, persistLiveEditor]);

  useEffect(() => {
    if (!activeRoute) return undefined;
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && event.key.toLowerCase() === "s" && isEditorRoute && current) {
        event.preventDefault();
        saveInPlace();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && event.key.toLowerCase() === "n") {
        event.preventDefault();
        createNext();
        return;
      }
      if (event.altKey && event.key === "ArrowLeft" && previous) {
        event.preventDefault();
        openInContext(previous);
      }
      if (event.altKey && event.key === "ArrowRight" && next) {
        event.preventDefault();
        openInContext(next);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeRoute, createNext, current, isEditorRoute, next, openInContext, previous, saveInPlace]);

  useEffect(() => {
    if (!isEditorRoute || !current) return undefined;

    const interceptEditorSave = (event) => {
      const button = event.target?.closest?.("button");
      if (!button) return;
      const saveIcon = button.querySelector?.(".lucide-save, [class*='lucide-save']");
      const saveLabel = String(button.textContent || "").toUpperCase().includes("REGISTRAR");
      if (!saveIcon && !saveLabel) return;
      event.preventDefault();
      event.stopPropagation();
      saveInPlace();
    };

    document.addEventListener("click", interceptEditorSave, true);
    return () => document.removeEventListener("click", interceptEditorSave, true);
  }, [current, isEditorRoute, saveInPlace]);

  useEffect(() => {
    if (!isEditorRoute) return undefined;
    const openRequested = (event) => openInContext(event.detail);
    window.addEventListener("trufapp:open-writing-text", openRequested);
    return () => window.removeEventListener("trufapp:open-writing-text", openRequested);
  }, [isEditorRoute, openInContext]);

  if (!activeRoute || !current) return null;

  const mobileDock = isMobile && isEditorRoute;
  const railStyle = mobileDock
    ? {
        ...styles.rail,
        left: "50%",
        right: "auto",
        top: "auto",
        bottom: "max(12px, env(safe-area-inset-bottom))",
        transform: "translateX(-50%)",
        flexDirection: "row",
        borderRadius: 18,
        padding: 6,
      }
    : styles.rail;
  const buttonStyle = mobileDock ? { ...styles.button, width: 48, height: 48 } : styles.button;

  return (
    <div style={railStyle} aria-label="Navegación del manuscrito">
      {saveFeedback && <div style={styles.feedback}>{saveFeedback}</div>}
      <button
        type="button"
        style={{ ...buttonStyle, opacity: previous && !busy ? 1 : 0.3 }}
        onClick={() => openInContext(previous)}
        disabled={!previous || busy}
        title="Texto anterior (Alt+←)"
        aria-label="Texto anterior"
      >
        <ChevronLeft size={20} />
      </button>

      {isEditorRoute && (
        <button
          type="button"
          style={buttonStyle}
          onClick={() => window.dispatchEvent(new Event("trufapp:toggle-binder"))}
          title="Abrir árbol de archivos"
          aria-label="Abrir árbol de archivos"
        >
          <PanelLeft size={20} />
        </button>
      )}

      <button
        type="button"
        style={{ ...buttonStyle, opacity: canEdit && !busy ? 1 : 0.3 }}
        onClick={createNext}
        disabled={!canEdit || busy}
        title="Nuevo texto siguiente (Ctrl/Cmd+N)"
        aria-label="Nuevo texto siguiente"
      >
        <FilePlus2 size={19} />
      </button>

      <button
        type="button"
        style={{ ...buttonStyle, opacity: next && !busy ? 1 : 0.3 }}
        onClick={() => openInContext(next)}
        disabled={!next || busy}
        title="Texto siguiente (Alt+→)"
        aria-label="Texto siguiente"
      >
        <ChevronRight size={20} />
      </button>
    </div>
  );
}

const styles = {
  rail: {
    position: "fixed",
    right: "max(10px, env(safe-area-inset-right))",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 1800,
    display: "flex",
    flexDirection: "column",
    gap: 5,
    padding: 5,
    borderRadius: 14,
    background: "rgba(18,16,14,0.82)",
    border: "1px solid rgba(212,175,55,0.22)",
    backdropFilter: "blur(14px)",
    boxShadow: "0 8px 28px rgba(0,0,0,0.35)",
  },
  button: {
    width: 40,
    height: 40,
    display: "grid",
    placeItems: "center",
    borderRadius: 11,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.06)",
    color: "#D4AF37",
    cursor: "pointer",
    touchAction: "manipulation",
  },
  feedback: {
    position: "absolute",
    left: "50%",
    bottom: "calc(100% + 8px)",
    transform: "translateX(-50%)",
    padding: "5px 9px",
    borderRadius: 9,
    background: "rgba(18,16,14,0.94)",
    border: "1px solid rgba(212,175,55,0.2)",
    color: "#D4AF37",
    fontSize: "0.72rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
    pointerEvents: "none",
  },
};
